import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { PhoneIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { AuthApi } from '../../services/authApi';
import { userRequiresPhone } from '../../utils/phoneGate';
import { getPostLoginRedirectUrl } from '../../utils/redirectUtils';
import SEOHead from '../../components/common/SEOHead';

const CompletePhonePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    user,
    isLoading,
    error,
    clearError,
    completePhone,
    logout,
    getAndClearRedirectUrl,
    redirectAfterLogin,
  } = useAuthStore();

  const [phone, setPhone] = useState('');
  const [fieldError, setFieldError] = useState('');

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user && !userRequiresPhone(user)) {
    const redirectUrl = getPostLoginRedirectUrl(redirectAfterLogin, '/student/my-courses');
    return <Navigate to={redirectUrl} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setFieldError('');

    if (!AuthApi.validatePhone(phone)) {
      setFieldError('Số điện thoại phải gồm 10 chữ số, bắt đầu bằng 0');
      return;
    }

    try {
      await completePhone(phone);
      const cleared = getAndClearRedirectUrl();
      const target = getPostLoginRedirectUrl(cleared, '/student/my-courses');
      navigate(target, { replace: true });
    } catch {
      // error in store
    }
  };

  return (
    <div className="min-h-screen bg-eh-keylime/40 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SEOHead
        title="Bổ sung số điện thoại | Training Center"
        description="Vui lòng nhập số điện thoại để hoàn tất đăng nhập."
        url="/auth/complete-phone"
      />

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center text-2xl font-semibold tracking-tight text-eh-forest-ink">
          M2MBA
        </div>
        <h2 className="eh-title mt-6 text-center">Bổ sung số điện thoại</h2>
        <p className="mt-2 text-center text-sm text-eh-muted">
          Tài khoản của bạn cần số điện thoại trước khi sử dụng hệ thống
          {user?.email ? (
            <>
              {' '}
              (<span className="text-eh-forest-ink">{user.email}</span>)
            </>
          ) : null}
          .
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="border border-eh-border bg-eh-cream px-4 py-8 shadow-eh-soft sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-m2-on-surface">
                Số điện thoại <span className="text-m2-error">*</span>
              </label>
              <div className="mt-1 relative">
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  required
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                    if (fieldError) setFieldError('');
                    if (error) clearError();
                  }}
                  className={`block w-full appearance-none rounded-eh border py-2.5 pl-10 pr-3 sm:text-sm ${
                    fieldError ? 'border-m2-error' : 'border-eh-border'
                  } bg-eh-cream placeholder:text-eh-muted focus:border-eh-forest-ink focus:outline-none focus:ring-1 focus:ring-eh-forest-ink/40`}
                  placeholder="0xxxxxxxxx"
                />
                <PhoneIcon className="absolute left-3 top-2.5 h-5 w-5 text-eh-muted" />
              </div>
              {fieldError && <p className="mt-1 text-sm text-m2-error">{fieldError}</p>}
            </div>

            {error && (
              <div className="rounded-md bg-m2-error/10 p-4">
                <div className="text-sm text-m2-error">{error}</div>
              </div>
            )}

            <button type="submit" disabled={isLoading} className={`eh-btn w-full ${isLoading ? 'opacity-60' : ''}`}>
              {isLoading ? 'Đang lưu...' : 'Tiếp tục'}
            </button>

            <button
              type="button"
              className="w-full text-sm text-eh-muted hover:text-eh-forest-ink"
              onClick={() => logout()}
            >
              Đăng xuất
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompletePhonePage;
